import { Link, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Rocket, Bell, Clock, Sparkles } from 'lucide-react';

const products = {
  'myhr-orbit': {
    name: 'MyHR Orbit',
    tagline: 'AI-Powered HR Management System',
    description: 'Revolutionize your HR operations with intelligent employee management, automated payroll processing, and AI-driven workforce analytics.',
    features: [
      'Smart Employee Onboarding',
      'AI-Powered Payroll Processing',
      'Leave & Attendance Automation',
      'Performance Analytics Dashboard'
    ],
    gradient: 'from-blue-600 to-indigo-600',
    icon: '👥'
  },
  'mysales-orbit': {
    name: 'MySales Orbit',
    tagline: 'AI-Powered Sales CRM & Pipeline Management',
    description: 'Supercharge your sales team with intelligent lead scoring, automated follow-ups, and AI-driven revenue forecasting to close more deals faster.',
    features: [
      'AI Lead Scoring & Prioritization',
      'Automated Sales Pipeline',
      'Smart Contract Management',
      'Revenue Forecasting Engine'
    ],
    gradient: 'from-orange-500 to-red-500',
    icon: '📈'
  },
  'myfinance-orbit': {
    name: 'MyFinance Orbit',
    tagline: 'AI-Powered Financial Management & Accounting',
    description: 'Transform your financial operations with intelligent invoicing, automated expense tracking, and AI-powered financial insights for smarter business decisions.',
    features: [
      'Smart Invoice Generation',
      'AI Expense Categorization',
      'Cash Flow Forecasting',
      'Automated Financial Reports'
    ],
    gradient: 'from-green-500 to-emerald-600',
    icon: '💰'
  },
  'mydoc-orbit': {
    name: 'MyDoc Orbit',
    tagline: 'AI-Powered Documentation Management',
    description: 'Transform your documentation workflows with intelligent document processing, AI-assisted content generation, and seamless collaboration tools.',
    features: [
      'AI-Powered Document Processing',
      'Smart Content Generation',
      'Automated Documentation',
      'Intelligent Search & Retrieval'
    ],
    gradient: 'from-purple-600 to-pink-600',
    icon: '📄'
  },
  'project-pulse': {
    name: 'ProjectPulse',
    tagline: 'AI Project Management & Analytics',
    description: 'Supercharge your project delivery with AI-driven insights, automated progress tracking, and intelligent resource allocation.',
    features: [
      'AI Project Planning',
      'Automated Progress Reports',
      'Risk Prediction Engine',
      'Resource Optimization'
    ],
    gradient: 'from-cyan-500 to-blue-600',
    icon: '📊'
  }
};

export default function ProductComingSoon() {
  const { productId } = useParams();
  const product = products[productId] || products['mydoc-orbit'];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col">
      {/* Header */}
      <header className="border-b border-slate-700/50 bg-slate-900/80 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_aurous-ecosystem/artifacts/x28qd6xo_30882183-9DD0-40B4-98FF-3BB81A460020.png" 
              alt="Aurous" 
              className="h-8 w-auto"
            />
          </Link>
          <Link to="/">
            <Button variant="ghost" className="text-slate-300 hover:text-white">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-16">
        <div className="max-w-2xl text-center">
          {/* Icon */}
          <div className="text-7xl mb-6">{product.icon}</div>

          {/* Badge */}
          <Badge className={`mb-6 bg-gradient-to-r ${product.gradient} text-white border-0 px-4 py-1.5`}>
            <Clock className="w-4 h-4 mr-2" />
            Coming Soon
          </Badge>

          {/* Title */}
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            {product.name}
          </h1>

          {/* Tagline */}
          <p className={`text-xl font-medium text-transparent bg-clip-text bg-gradient-to-r ${product.gradient} mb-6`}>
            {product.tagline}
          </p>

          {/* Description */}
          <p className="text-lg text-slate-400 mb-10 leading-relaxed">
            {product.description}
          </p>

          {/* Features Preview */}
          <div className="bg-slate-800/50 rounded-2xl p-6 mb-10 border border-slate-700/50">
            <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wide mb-4 flex items-center justify-center gap-2">
              <Sparkles className="w-4 h-4" />
              Upcoming Features
            </h3>
            <div className="grid grid-cols-2 gap-4">
              {product.features.map((feature, index) => (
                <div key={index} className="flex items-center gap-2 text-slate-300">
                  <Rocket className="w-4 h-4 text-blue-400 flex-shrink-0" />
                  <span className="text-sm">{feature}</span>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className={`bg-gradient-to-r ${product.gradient} hover:opacity-90 text-white`}
              disabled
            >
              <Bell className="w-5 h-5 mr-2" />
              Notify Me When Ready
            </Button>
            <Link to="/#contact">
              <Button size="lg" variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-800">
                Contact Sales
              </Button>
            </Link>
          </div>

          {/* Estimated Launch */}
          <p className="mt-8 text-slate-500 text-sm">
            Estimated launch: Q2 2026
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-700/50 py-6">
        <div className="max-w-7xl mx-auto px-4 text-center text-slate-500 text-sm">
          © 2026 Aurous Consultancy. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
